import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPut } from "lib/adminClient";

const inputCls =
  "w-full px-2.5 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";

const blankResult = () => ({ year: "", appear: "", gpa5: "", pass: "", award: "—" });
const blankTopper = () => ({ name: "", gpa: "5.00", group: "", year: "" });

// Editor for one results table (SSC or HSC).
function ResultTable({ title, rows, setRows }) {
  const upd = (i, f, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [f]: v } : r)));
  const move = (i, dir) =>
    setRows((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const c = [...arr];
      [c[i], c[j]] = [c[j], c[i]];
      return c;
    });
  return (
    <AdminCard className="mb-[18px]">
      <AdminCardHead
        title={title}
        action={
          <button onClick={() => setRows([...rows, blankResult()])} className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90">
            + Add Year
          </button>
        }
      />
      <div className="px-[22px] pt-3 hidden md:grid grid-cols-[1fr_1fr_1fr_1fr_1.4fr_auto] gap-2 text-[11px] font-bold text-[#9aa] uppercase tracking-[0.04em]">
        <span>Year</span><span>Appeared</span><span>GPA-5</span><span>Pass rate</span><span>Award</span><span></span>
      </div>
      <div className="p-[22px] pt-2 flex flex-col gap-2">
        {rows.length === 0 && <div className="text-muted text-sm">No rows yet.</div>}
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-2 md:grid-cols-[1fr_1fr_1fr_1fr_1.4fr_auto] gap-2 items-center">
            <input value={r.year} onChange={(e) => upd(i, "year", e.target.value)} placeholder="2024" className={inputCls} />
            <input value={r.appear} onChange={(e) => upd(i, "appear", e.target.value)} placeholder="312" className={inputCls} />
            <input value={r.gpa5} onChange={(e) => upd(i, "gpa5", e.target.value)} placeholder="298" className={inputCls} />
            <input value={r.pass} onChange={(e) => upd(i, "pass", e.target.value)} placeholder="99.4%" className={inputCls} />
            <input value={r.award} onChange={(e) => upd(i, "award", e.target.value)} placeholder="Board Stand 1st (or —)" className={inputCls} />
            <div className="flex gap-1">
              <button onClick={() => move(i, -1)} title="Up" className="bg-[#f2f4f6] border-none rounded-md px-2 py-1.5 cursor-pointer text-xs">↑</button>
              <button onClick={() => setRows(rows.filter((_, idx) => idx !== i))} title="Remove" className="bg-[#fdeaea] border-none rounded-md px-2 py-1.5 cursor-pointer text-xs text-[#c44]">🗑</button>
            </div>
          </div>
        ))}
      </div>
    </AdminCard>
  );
}

// Coerce numeric-looking fields to numbers so the public JSON stays clean.
const num = (v) => (v !== "" && !isNaN(v) ? Number(v) : v);

export default function ResultsPage() {
  const [ssc, setSsc] = useState([]);
  const [hsc, setHsc] = useState([]);
  const [toppers, setToppers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const [loadFailed, setLoadFailed] = useState(false);


  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      const d = (await adminGet("/admin/content/results")) || {};
      setSsc(d.ssc || []);
      setHsc(d.hsc || []);
      setToppers(d.toppers || []);
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updTop = (i, f, v) => setToppers(toppers.map((t, idx) => (idx === i ? { ...t, [f]: v } : t)));

  const save = async () => {
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    setSaving(true);
    setStatusMsg("");
    setError("");
    try {
      const cleanRows = (arr) =>
        arr.filter((r) => String(r.year).trim()).map((r) => ({ ...r, appear: num(r.appear), gpa5: num(r.gpa5), award: r.award || "—" }));
      const payload = {
        ssc: cleanRows(ssc),
        hsc: cleanRows(hsc),
        toppers: toppers.filter((t) => (t.name || "").trim()),
      };
      const saved = await adminPut("/admin/content/results", payload);
      if (saved) {
        setSsc(saved.ssc || []);
        setHsc(saved.hsc || []);
        setToppers(saved.toppers || []);
      }
      setStatusMsg("Saved.");
      setTimeout(() => setStatusMsg(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn">
      {loadFailed && <LoadFailedBanner onRetry={load} />}

      {error && <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>}

      <ResultTable title="SSC Results" rows={ssc} setRows={setSsc} />
      <ResultTable title="HSC Results" rows={hsc} setRows={setHsc} />

      {/* Toppers */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Board Toppers"
          action={
            <button onClick={() => setToppers([...toppers, blankTopper()])} className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90">
              + Add Topper
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-2">
          {toppers.length === 0 && <div className="text-muted text-sm">No toppers yet.</div>}
          {toppers.map((t, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-[2fr_1fr_1.3fr_1fr_auto] gap-2 items-center">
              <input value={t.name} onChange={(e) => updTop(i, "name", e.target.value)} placeholder="Student name" className={inputCls} />
              <input value={t.gpa} onChange={(e) => updTop(i, "gpa", e.target.value)} placeholder="5.00" className={inputCls} />
              <input value={t.group} onChange={(e) => updTop(i, "group", e.target.value)} placeholder="Science" className={inputCls} />
              <input value={t.year} onChange={(e) => updTop(i, "year", e.target.value)} placeholder="2024" className={inputCls} />
              <button onClick={() => setToppers(toppers.filter((_, idx) => idx !== i))} title="Remove" className="bg-[#fdeaea] border-none rounded-md px-2 py-1.5 cursor-pointer text-xs text-[#c44]">🗑</button>
            </div>
          ))}
        </div>
      </AdminCard>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} className="bg-green text-white border-none rounded-[9px] px-[26px] py-[11px] font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60">
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {statusMsg && <span className="text-[13px] text-[#1a9f5e] font-semibold">{statusMsg}</span>}
      </div>
    </div>
  );
}
